import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation, Trans } from 'react-i18next';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { Gift, GraduationCap, CalendarDays, ShieldCheck, Bookmark } from 'lucide-react';
import logo from '../../utils/static/logo2.png';

const BENEFIT_ICONS = {
  1: Gift,
  2: GraduationCap,
  3: CalendarDays,
  4: ShieldCheck,
  5: Bookmark,
};

const BENEFIT_KEYS = [1, 2, 3, 4, 5];

const LogoHighlight = () => (
  <img
    src="https://res.cloudinary.com/dommpgy1l/image/upload/v1784401650/somosmas_logo_horizontal_nlqydy.png"
    alt="SOMOS+"
    className="h-14 w-auto"
  />
);

const ComunidadModal = ({ onClose }) => {
  const { t } = useTranslation();
  const closeBtnRef = useRef(null);

  // Lock body scroll (iOS-correct: position fixed + scroll restoration)
  useEffect(() => {
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      window.scrollTo(0, scrollY);
    };
  }, []);

  // ESC to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Initial focus on close button
  useEffect(() => {
    closeBtnRef.current?.focus();
  }, []);

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="comunidad-modal-title"
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <button
          ref={closeBtnRef}
          onClick={onClose}
          aria-label={t('landing.comunidad-modal.close')}
          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1d5cab] transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
        >
          <XMarkIcon className="h-5 w-5" aria-hidden="true" />
        </button>

        <div className="p-8 sm:p-10">
          <p className="text-xs font-bold uppercase tracking-widest text-[#1d5cab] mb-1">
            {t('landing.comunidad-modal.label')}
          </p>
          <h2
            id="comunidad-modal-title"
            className="text-sm sm:text-xl font-extrabold text-gray-900 mb-4 leading-tight"
          >
            <Trans
              i18nKey="landing.comunidad-modal.heading"
              components={{ highlight: <span className="text-[#1d5cab]" /> }}
            />
          </h2>

          <p className="text-4xl leading-7 text-gray-600 mb-6">
            <LogoHighlight />
          </p>

          <p className="text-sm leading-7 text-gray-600 mb-8">
            {t('landing.comunidad-modal.intro')}
          </p>

          <div className="w-[10%] h-0.5 bg-[#F5751B] rounded-full mb-4" />

          <div className="pt-8 mb-8">
            <h3 className="text-lg font-bold text-gray-900 mb-6 uppercase text-center">
              {t('landing.comunidad-modal.benefits-heading')}
            </h3>
            <div className="space-y-4">
              {BENEFIT_KEYS.map(n => {
                const Icon = BENEFIT_ICONS[n];
                return (
                  <div key={n} className="rounded-xl bg-[#f0efed] p-4 flex gap-3">
                    <Icon className="shrink-0 mt-0.5 text-[#1d5cab]" size={18} aria-hidden="true" />
                    <div>
                      <p className="text-xs font-bold text-[#1d5cab] mb-1">
                        {t(`landing.comunidad-modal.benefit-${n}-title`)}
                      </p>
                      <p className="text-xs text-gray-600 leading-relaxed">
                        {t(`landing.comunidad-modal.benefit-${n}-desc`)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="rounded-2xl bg-[#1d5cab] text-white p-6 mb-8">
            <h3 className="text-sm font-bold uppercase tracking-wide mb-3">
              {t('landing.comunidad-modal.impact-title')}
            </h3>
            <p className="text-sm leading-7 opacity-90">
              {t('landing.comunidad-modal.impact-desc')}
            </p>
          </div>


          <div className="flex justify-center mb-4">
            <img src={logo} alt="Brand logo" className="h-7 w-auto" />
          </div>

          <p className="text-center text-sm font-extrabold text-gray-900 tracking-wide mb-4">
            {t('landing.comunidad-modal.tagline')}
          </p>
          <div className="flex justify-center">
            <a href="/map" onClick={onClose} className="inline-block rounded-md bg-[#1d5cab] px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#2372bd] transition-colors">
              {t('landing.comunidad-modal.explore-map')}
            </a>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ComunidadModal;
